import { useState, useEffect, useCallback } from 'react';
import { comedorApi } from '../api/comedorApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de comedores
 * Proporciona estado y funciones para operaciones CRUD de comedores
 */
export const useComedores = () => {
  const [comedores, setComedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Función helper para recargar comedores manteniendo el filtrado por proyecto
   */
  const reloadComedores = useCallback(async () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    if (selectedProject && selectedProject.id) {
      const response = await comedorApi.getByProyecto(selectedProject.id);
      setComedores(response.data);
    } else {
      const response = await comedorApi.getAll();
      setComedores(response.data);
    }
  }, []);

  /**
   * Obtiene todos los comedores o por proyecto
   */
  const fetchComedores = useCallback(async (idProyecto = null, idComedor = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.getAll(idProyecto, idComedor);
      setComedores(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar comedores');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un comedor específico por ID
   */
  const fetchComedorById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el comedor');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo comedor
   */
  const createComedor = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.create(data);
      showSuccessToast('Comedor creado exitosamente');
      
      // Refrescar la lista de comedores manteniendo el filtrado por proyecto
      await reloadComedores();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el comedor');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reloadComedores]);

  /**
   * Actualiza un comedor existente
   */
  const updateComedor = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.update(id, data);
      showSuccessToast('Comedor actualizado exitosamente');
      
      // Refrescar la lista de comedores manteniendo el filtrado por proyecto
      await reloadComedores();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el comedor');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reloadComedores]);

  /**
   * Elimina un comedor
   */
  const deleteComedor = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await comedorApi.delete(id);
      showSuccessToast('Comedor eliminado exitosamente');
      
      // Refrescar la lista de comedores manteniendo el filtrado por proyecto
      await reloadComedores();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el comedor');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reloadComedores]);

  /**
   * Obtiene comedores por proyecto y actualiza el estado (query parameter)
   */
  const fetchComedoresByProyecto = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.getByProyecto(proyectoId);
      setComedores(response.data); // Actualizar el estado con los comedores filtrados
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar comedores del proyecto');
      setError(errorMessage);
      setComedores([]); // Limpiar comedores en caso de error
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene comedores por proyecto usando resource nesting (para carga masiva)
   */
  const fetchComedoresByProyectoRest = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.getByProyectoRest(proyectoId);
      setComedores(response.data); // Actualizar el estado con los comedores filtrados
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar comedores del proyecto');
      setError(errorMessage);
      setComedores([]); // Limpiar comedores en caso de error
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene los tipos de servicio asociados a un comedor específico
   */
  const fetchTipoServiciosPorComedor = useCallback(async (comedorId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comedorApi.getTipoServiciosPorComedor(comedorId);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipos de servicio del comedor');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Nota: No se cargan comedores automáticamente.
  // Los componentes deben llamar explícitamente a fetchComedores() o fetchComedoresByProyecto()

  return {
    // Estado
    comedores,
    loading,
    error,
    
    // Funciones
    fetchComedores,
    fetchComedorById,
    fetchComedoresByProyecto,
    fetchComedoresByProyectoRest,
    fetchTipoServiciosPorComedor,
    createComedor,
    updateComedor,
    deleteComedor,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useComedores;
